import { motion } from "framer-motion";
import { Reveal, EASE } from "@/lib/motion";
import { PageHero, PrimaryCTAs, SectionHeader, FaqBlock } from "@/components/site/PageParts";
import { CTABand } from "@/components/site/Bands";
import { SEO, breadcrumbSchema, faqSchema, serviceSchema } from "@/components/site/SEO";
import { RelatedWork } from "@/components/site/RelatedWork";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "Conversion Content", path: "/services/conversion-content" },
];

const RELATED = [
  { tag: "Case Study", title: "inFeedo — multi-format research content", desc: "Research-driven assets across emails, decks, and ebooks.", to: "/case-studies" },
  { tag: "Service", title: "SEO/AEO/GEO Blog Content", desc: "Top-of-funnel traffic that feeds these conversion assets.", to: "/services/blog-content" },
  { tag: "Process", title: "The Dandelion Method", desc: "How every asset gets built and integrated.", to: "/how-we-work" },
];

const GAPS = [
  "Traffic is fine, but conversion is stuck at 1–2%.",
  "\"Has this worked for companies like us?\" — no answer.",
  "\"How do you compare to X?\" — no answer.",
  "\"What's the ROI?\" — no answer.",
  "\"How do we actually implement this?\" — no answer.",
];

const ASSETS = [
  {
    n: "01", title: "Case Studies",
    format: "Challenge → Solution → Results. Written from real buyer interviews, with a before/after metrics table and pull quotes.",
    length: "1,500–2,000 words", price: "$1,200–$1,800", time: "2–3 weeks",
  },
  {
    n: "02", title: "Whitepapers & eBooks",
    format: "In-depth strategic guides, structured and designed. Not just long blog posts.",
    length: "5,000–8,000 words", price: "$3,500–$5,500", time: "4–6 weeks",
  },
  {
    n: "03", title: "Comparison Guides",
    format: "Honest, detailed comparisons that control the narrative and build trust through transparency.",
    length: "2,500–3,500 words", price: "$1,500–$2,200", time: "2–3 weeks",
  },
  {
    n: "04", title: "Interactive Tools & Calculators",
    format: "ROI / TCO calculators and timeline estimators. Generate qualified leads and shorten sales cycles.",
    length: "Includes development", price: "$2,500–$4,000", time: "3–4 weeks",
  },
  {
    n: "05", title: "Email Nurture Sequences",
    format: "5–7 emails per funnel stage. Educational, not sales-y.",
    length: "5–7 emails", price: "$1,200–$1,800 / sequence", time: "2 weeks",
  },
];

const PROCESS = [
  { n: "01", title: "Discovery", body: "Understand your sales process and the objections your buyers actually raise." },
  { n: "02", title: "Research & Interviews", body: "Customer and sales team interviews — or competitive and industry research when interviews aren't the fit." },
  { n: "03", title: "Content Creation", body: "Outline → draft → review → revisions. Owned end-to-end." },
  { n: "04", title: "Design & Delivery", body: "Optional PDF design and landing page copy for gated assets." },
];

const FAQS = [
  { q: "Should we gate these assets?", a: "Depends on the asset. Case studies and comparison guides work well ungated as sales tools. Whitepapers and calculators work better gated as lead magnets." },
  { q: "Can you help identify which customers to feature?", a: "Yes — we work with your customer success team to find strong-result, willing, on-ICP customers." },
  { q: "Do you handle customer interviews for case studies?", a: "Yes. We coordinate scheduling and run a 30–45 minute interview ourselves." },
];

export default function ConversionContent() {
  return (
    <div data-testid="service-conversion-page">
      <SEO
        title="Conversion-Focused Assets — Case Studies, Whitepapers, Guides"
        description="Case studies, whitepapers, comparison guides, calculators, and email sequences engineered to convert high-intent B2B SaaS buyers into pipeline."
        path="/services/conversion-content"
        jsonLd={[breadcrumbSchema(CRUMBS), faqSchema(FAQS), serviceSchema({ name: "Conversion-Focused Assets", description: "Bottom-of-funnel assets that move B2B SaaS buyers to a decision.", path: "/services/conversion-content" })]}
      />
      <PageHero
        eyebrow="Services · Conversion Assets"
        lines={["Content That Converts", "Browsers Into"]}
        italicLast="Buyers."
        sub="Blog content drives traffic. Conversion assets drive revenue. Case studies, whitepapers, comparison guides — these are the pieces that capture high-intent buyers and turn them into qualified pipeline."
        crumbs={CRUMBS}
      >
        <PrimaryCTAs />
      </PageHero>

      {/* The gap */}
      <section className="section-pad bg-[#F5F5F5]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="The gap" title="Traffic is fine. Conversion is where deals die." />
            <p className="mt-6 max-w-md text-[#666666] leading-relaxed">
              Bottom-of-funnel questions go unanswered — and prospects leave silently.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="border-t border-[#E0E0E0]">
              {GAPS.map((g, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.05, ease: EASE }}
                  className="py-5 border-b border-[#E0E0E0] flex items-start gap-4 text-[#111111]"
                >
                  <span className="font-mono text-xs text-[#999999] mt-2">0{i + 1}</span>
                  <span className="text-lg leading-relaxed italic">&ldquo;{g}&rdquo;</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Assets */}
      <section className="section-pad bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeader eyebrow="What we create" title="Five conversion assets we ship." />
          <div className="mt-14 border-t border-[#111111]">
            {ASSETS.map((a, i) => (
              <motion.div
                key={a.n}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.04, ease: EASE }}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-10 md:py-12 border-b border-[#E0E0E0]"
              >
                <div className="md:col-span-1">
                  <span className="font-serif text-4xl md:text-5xl text-[#E0E0E0]">{a.n}</span>
                </div>
                <div className="md:col-span-5">
                  <h3 className="font-serif text-2xl md:text-3xl text-[#111111]">{a.title}</h3>
                  <p className="mt-4 text-[#666666] leading-relaxed">{a.format}</p>
                </div>
                <div className="md:col-span-6 grid grid-cols-3 gap-4 self-end">
                  {[["Length", a.length], ["Investment", a.price], ["Timeline", a.time]].map(([k, v]) => (
                    <div key={k} className="border-t border-[#111111] pt-4">
                      <p className="text-xs uppercase tracking-[0.15em] text-[#999999]">{k}</p>
                      <p className="mt-2 font-semibold text-[#111111]">{v}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-pad bg-[#111111] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#666666] mb-6 font-mono">Process · 4 phases</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">How each asset gets built.</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#333333]">
            {PROCESS.map((p) => (
              <Reveal key={p.n}>
                <div className="p-8 border-r border-b border-[#333333] h-full">
                  <span className="font-mono text-xs text-[#666666]">{p.n}</span>
                  <h3 className="mt-4 font-serif text-2xl text-white">{p.title}</h3>
                  <p className="mt-4 text-[#E0E0E0] leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad bg-[#F5F5F5]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeader eyebrow="FAQ" title="Common questions." />
          <div className="mt-12">
            <FaqBlock items={FAQS} />
          </div>
        </div>
      </section>

      <CTABand heading="Turn traffic into signed contracts." />
      <RelatedWork items={RELATED} />
    </div>
  );
}
